/** Sustainability (Demo Mode only) - illustrative landing dashboard for the new Sustainability
 * workspace covering wider ESG considerations. No backend carbon/energy/water feed exists yet
 * - all figures are static mock data, matching the same "seeded illustrative numbers" approach
 * used by src/pages/FinanceDashboard. Links into existing real Waste Stock and Supplier
 * Governance screens rather than duplicating that data. */

import { useState } from 'react';
import { Box, Button, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { Co2, Bolt, Recycling, WaterDrop, Groups } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const MOCK_ESG_INITIATIVES = [
  { initiative: 'Reduce packaging waste', target: '-15% by 2027', progress: '62%', status: 'On Track' },
  { initiative: 'Renewable energy sourcing', target: '50% of stores', progress: '34%', status: 'At Risk' },
  { initiative: 'Supplier ESG assessments', target: '100% of tier-1 suppliers', progress: '78%', status: 'On Track' },
  { initiative: 'Water usage reduction', target: '-10% by 2027', progress: '41%', status: 'On Track' },
];

const Sustainability = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const [initiativesPage, setInitiativesPage] = useState(0);

  const TILES = [
    { label: t('sustainabilityCarbonFootprint', 'Carbon footprint (tCO2e)'), value: '412', subtitle: t('sustainabilityCarbonFootprintSub', 'Year to date'), icon: <Co2 />, color: '#2e7d32' },
    { label: t('sustainabilityEnergyUsage', 'Energy usage (kWh)'), value: '86,400', subtitle: t('sustainabilityEnergyUsageSub', 'Across the estate'), icon: <Bolt />, color: '#ed6c02' },
    { label: t('sustainabilityWasteDiversion', 'Waste diversion rate'), value: '58%', subtitle: t('sustainabilityWasteDiversionSub', 'Diverted from landfill'), icon: <Recycling />, color: '#1976d2' },
    { label: t('sustainabilityWaterUsage', 'Water usage (m3)'), value: '1,240', subtitle: t('sustainabilityWaterUsageSub', 'Across the estate'), icon: <WaterDrop />, color: '#0288d1' },
    { label: t('sustainabilitySupplierEsgScore', 'Supplier ESG score'), value: '76/100', subtitle: t('sustainabilitySupplierEsgScoreSub', 'Tier-1 suppliers'), icon: <Groups />, color: '#9c27b0' },
  ];

  return (
    <PageArea>
      <PageTitle>
        {t('sustainabilityTitle', 'Sustainability')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={2.4}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('sustainabilityInitiativesTitle', 'ESG initiatives')}
            <Pill text={`${MOCK_ESG_INITIATIVES.length} ${t('sustainabilityInitiativesLabel', 'initiatives')}`} />
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', flexShrink: 0 }}>
              <Button
                variant="secondary"
                size="lg"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => navigate('/store/waste-logs')}
              >
                {t('sustainabilityViewWaste', 'Waste & Recycling')}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => navigate('/procurement/suppliers')}
              >
                {t('sustainabilityViewSupplierEsg', 'Supplier ESG')}
              </Button>
            </Box>
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('sustainabilityInitiative', 'Initiative')}</TableCell>
                  <TableCell>{t('sustainabilityTarget', 'Target')}</TableCell>
                  <TableCell>{t('sustainabilityProgress', 'Progress')}</TableCell>
                  <TableCell>{t('sustainabilityStatus', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_ESG_INITIATIVES.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.initiative}</TableCell>
                    <TableCell>{row.target}</TableCell>
                    <TableCell>{row.progress}</TableCell>
                    <TableCell>{row.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={initiativesPage}
            setPage={setInitiativesPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default Sustainability;
